import React, { InputHTMLAttributes, useRef, useCallback } from 'react';
import cs from 'classnames';
import s from './InputGroups.module.css';

export interface InputGroupProps extends InputHTMLAttributes<HTMLInputElement> {
    labelText?: string;
    labelHidden?: boolean;
    labelClassName?: string;
    hideLabelMargin?: boolean;
    variant?: 'large' | 'small';
    name?: string;
    hasError?: boolean;
    rightIcon?: React.ReactNode;
    leftIcon?: React.ReactNode;
    disabledStyleHidden?: boolean;
    normalize?: (val: any) => any;
    formatter?: (val: any) => any;
}

export const InputGroup = React.forwardRef(
    (
        {
            id,
            name,
            type = 'text',
            variant = 'large',
            placeholder,
            labelText,
            labelClassName = 'text-sm font-medium',
            labelHidden,
            className = 'text-base',
            hasError,
            disabledStyleHidden,
            rightIcon,
            leftIcon,
            hideLabelMargin = false,
            onChange: formikOnChange,
            onBlur: formikOnBlur,
            normalize,
            formatter,
            ...rest
        }: InputGroupProps,
        ref: React.ForwardedRef<HTMLInputElement>,
    ) => {
        const rootLabelClass = cs(s.label, labelClassName, { 'sr-only': labelHidden });
        const rootClassName = cs(className, {
            [s.root]: !leftIcon,
            [s.base]: !hasError,
            ['disabled:bg-gray-50']: !disabledStyleHidden,
            [s.large]: variant === 'large',
            [s.small]: variant === 'small',
            [s.rootLeftIcon]: !!leftIcon,
            [s.error]: hasError,
        });

        const onChange = useRef((e: React.ChangeEvent<any>) => {
            if (normalize) {
                e.target.value = normalize(e.target.value);
            }
            formikOnChange?.(e);
        }).current;

        const onBlur = useCallback((e: React.FocusEvent<any>) => {
            if (e.target.value && formatter) {
                e.target.value = formatter?.(e.target.value);
                formikOnChange?.(e);
            }
            formikOnBlur?.(e);
        }, []);

        return (
            <div>
                <label htmlFor={id} className={rootLabelClass}>
                    {labelText}
                </label>
                <div className={`${hideLabelMargin ? '' : 'mt-1'} relative`}>
                    {leftIcon && leftIcon}
                    <input
                        ref={ref}
                        type={type}
                        name={name}
                        id={id}
                        onChange={onChange}
                        onBlur={onBlur}
                        className={rootClassName}
                        placeholder={placeholder}
                        {...rest}
                    />
                    {rightIcon && rightIcon}
                </div>
            </div>
        );
    },
);

InputGroup.displayName = 'InputGroupComponenet';
